import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import PageHeader from "../../../../Ui_Components/PageHeader";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";
import {
  calculateCPFRatio,
  formatCPFRatio,
} from "../../../../Plans/Meal_Creation/CPFCalculator";
import { BASE_URL } from "../../../../api/url";
import { getIngredientList } from "../../../../../services/clientDetails";
import { ReactComponent as VegSvg } from "../../../../../assects/svg/Recipe/Veg.svg";
import { ReactComponent as NonVegSvg } from "../../../../../assects/svg/Recipe/Non_Veg.svg";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface Recipe {
  recipe_id: number;
  recipe_name: string;
  recipe_image: string | null;
  is_veg: boolean;
  cooking_types: string[];
  meal_times: string[];
  serving_size: string;
  cooking_time: string;
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
  cpf: string;
  raw: {
    gl: string;
    adjprotein: string;
    recipe_method: string;
    recipe_of_servings: string;
    recipe_dish_category: string;
    recipe_cooking_type: string;
    recipe_meal_time: string;
    cooked_qty: string;
    "measurements unit": string;
    [key: string]: any;
  };
}

// ─── API ingredient shape ─────────────────────────────────────────────────────
interface ApiIngredient {
  ingredient_id: number;
  ingredient_name: string;
  ingredient_picture: string;
  ingredient_quantity: string;
  ingredient_units: string;
  ingredient_cal_cals: string;
  ingredient_cal_carbs: string;
  ingredient_cal_hidden_fat: string;
  ingredient_cal_AAFAdj_protein: string;
  ingredient_cal_GL: string;
  ingredient_cal_oil: string;
}

interface IngredientApiResponse {
  success: string;
  message: string;
  total_data: number;
  data: ApiIngredient[];
}

// ─── Normalised ingredient ────────────────────────────────────────────────────
interface Ingredient {
  id: number;
  name: string;
  image: string | null;
  quantity: string;
  unit: string;
  cal: string;
  carb: string;
  fats: string;
  aProt: string;
  gl: string;
  oil: string;
}

const normaliseIngredient = (r: ApiIngredient): Ingredient => ({
  id: r.ingredient_id,
  name: r.ingredient_name,
  image:
    r.ingredient_picture && r.ingredient_picture.trim() !== ""
      ? r.ingredient_picture
      : null,
  quantity: r.ingredient_quantity,
  unit: r.ingredient_units,
  cal: r.ingredient_cal_cals,
  carb: r.ingredient_cal_carbs,
  fats: r.ingredient_cal_hidden_fat,
  aProt: r.ingredient_cal_AAFAdj_protein,
  gl: r.ingredient_cal_GL,
  oil: r.ingredient_cal_oil,
});

// ─── Shimmer ──────────────────────────────────────────────────────────────────
const shimmerStyles = `
  @keyframes shimmer {
    0%   { background-position: -1000px 0; }
    100% { background-position:  1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

// ─── Fallback recipe ──────────────────────────────────────────────────────────
const FALLBACK_RECIPE: Recipe = {
  recipe_id: 0,
  recipe_name: "Recipe",
  recipe_image: null,
  is_veg: true,
  cooking_types: [],
  meal_times: [],
  serving_size: "-",
  cooking_time: "-",
  calories: 0,
  carbs: 0,
  protein: 0,
  fat: 0,
  cpf: "-",
  raw: {
    gl: "0",
    adjprotein: "0",
    recipe_method: "",
    recipe_of_servings: "-",
    recipe_dish_category: "-",
    recipe_cooking_type: "",
    recipe_meal_time: "",
    cooked_qty: "0",
    "measurements unit": "",
  },
};

// ─── Canvas text measurement ──────────────────────────────────────────────────
let _canvas: HTMLCanvasElement | null = null;
function measureTextWidth(text: string, font: string): number {
  if (!_canvas) _canvas = document.createElement("canvas");
  const ctx = _canvas.getContext("2d")!;
  ctx.font = font;
  return ctx.measureText(text).width;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
interface TooltipProps {
  items: string[];
  anchorRef: React.RefObject<HTMLSpanElement | null>;
  visible: boolean;
}

const ItemTooltip: React.FC<TooltipProps> = ({ items, anchorRef, visible }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!visible || !anchorRef.current || !tooltipRef.current) return;
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 6;
    let top = anchorRect.bottom + gap + window.scrollY;
    let left = anchorRect.left + window.scrollX;
    if (anchorRect.bottom + gap + tooltipRect.height > window.innerHeight)
      top = anchorRect.top - tooltipRect.height - gap + window.scrollY;
    if (left + tooltipRect.width > window.innerWidth - 8)
      left = window.innerWidth - tooltipRect.width - 8 + window.scrollX;
    setPos({ top, left });
  }, [visible, anchorRef, items]);

  if (!visible) return null;

  return (
    <div
      ref={tooltipRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        border: "0.5px solid #F3F4F6",
      }}
      className="bg-white rounded-lg px-2 py-2 shadow-sm inline-flex max-w-[240px] pointer-events-none"
    >
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span
            key={i}
            className="text-[8px] 2xl:text-[10px] font-inter px-3 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: "#F0F0F0", color: "#1F1F1F" }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── TruncatedList ────────────────────────────────────────────────────────────
const TruncatedList: React.FC<{ items: string[]; font?: string }> = ({
  items,
  font = "12px Inter, ui-sans-serif, system-ui, sans-serif",
}) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [hovered, setHovered] = useState(false);
  const isTruncated = visibleCount < items.length;

  useLayoutEffect(() => {
    if (!containerRef.current || items.length === 0) return;
    const calculate = () => {
      const containerWidth = containerRef.current!.offsetWidth;
      if (!containerWidth) return;
      const sepWidth = measureTextWidth(", ", font);
      let usedWidth = 0;
      let count = 0;
      for (let i = 0; i < items.length; i++) {
        const itemWidth = measureTextWidth(items[i], font);
        const remaining = items.length - i - 1;
        const badgeWidth =
          remaining > 0 ? measureTextWidth(` +${remaining}`, font) : 0;
        const ahead = i === 0 ? 0 : sepWidth;
        if (usedWidth + ahead + itemWidth + badgeWidth <= containerWidth) {
          usedWidth += ahead + itemWidth;
          count = i + 1;
        } else break;
      }
      setVisibleCount(Math.max(1, count));
    };
    calculate();
    const ro = new ResizeObserver(calculate);
    ro.observe(containerRef.current!);
    return () => ro.disconnect();
  }, [items, font]);

  const visible = items.slice(0, visibleCount);
  const remaining = items.length - visibleCount;

  if (items.length === 0) {
    return (
      <span className="text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F]">
        -
      </span>
    );
  }

  return (
    <>
      <span
        ref={containerRef}
        onMouseEnter={() => isTruncated && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`block w-full overflow-hidden whitespace-nowrap text-[#1F1F1F] text-xs 3xl:text-sm font-inter font-medium ${
          isTruncated ? "cursor-default" : ""
        }`}
      >
        {visible.join(", ")}
        {remaining > 0 && (
          <span className="text-[#0DB5B5] font-medium"> +{remaining}</span>
        )}
      </span>
      <ItemTooltip
        items={items}
        anchorRef={containerRef}
        visible={hovered && isTruncated}
      />
    </>
  );
};

// ─── VegIcon ──────────────────────────────────────────────────────────────────
const VegIcon: React.FC<{ isVeg: boolean }> = ({ isVeg }) => (
  <span
    title={isVeg ? "Vegetarian" : "Non-vegetarian"}
    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, flexShrink: 0, overflow: "visible" }}
  >
    {isVeg
      ? <VegSvg style={{ width: 16, height: 16, display: "block", overflow: "visible" }} />
      : <NonVegSvg style={{ width: 16, height: 16, display: "block", overflow: "visible" }} />
    }
  </span>
);

// ─── Sub-components ───────────────────────────────────────────────────────────
const IngredientThumb: React.FC<{ name: string; image: string | null }> = ({
  name,
  image,
}) => {
  if (image) {
    return (
      <img
        src={image.startsWith("http") ? image : `${BASE_URL}${image}`}
        alt={name}
        className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M8 16l2.5-3.5L13 15l2-2.5L18 16H8z" fill="#9CA3AF" />
        <circle cx="9.5" cy="10.5" r="1.5" fill="#9CA3AF" />
      </svg>
    </div>
  );
};

// ─── Page component ───────────────────────────────────────────────────────────
const RecipeDetailPage: React.FC = () => {
  useCustomScrollbar();
  const { userId } = useParams<{ userId: string; recipeId: string }>();
  const location = useLocation();

  // ── Search & ingredient state ──
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [allIngredients, setAllIngredients] = useState<Ingredient[]>([]);
  const [totalIngredientCount, setTotalIngredientCount] = useState(0);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [ingredientError, setIngredientError] = useState<string | null>(null);

  const recipe: Recipe =
    (location.state as { recipe?: Recipe })?.recipe ?? FALLBACK_RECIPE;

  const gl = parseFloat(recipe.raw.gl) || 0;
  const aProt = parseFloat(recipe.raw.adjprotein) || 0;
 const methodEncoded = recipe.raw.recipe_method?.trim() || "";
const method = methodEncoded ? decodeBase64Text(methodEncoded) : "";

  const cpfDisplay =
    recipe.cpf !== "-"
      ? recipe.cpf
      : formatCPFRatio(
          calculateCPFRatio(
            recipe.carbs,
            recipe.protein,
            recipe.fat,
            recipe.calories,
          ).ratio,
        );

  // ─── Debounce search input (400 ms) ──────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ─── Fetch ingredients from API ───────────────────────────────────────────
  const fetchIngredients = useCallback(
    async (search: string) => {
      if (!recipe.recipe_id) return;
      setLoadingIngredients(true);
      setIngredientError(null);
      try {
        const json: IngredientApiResponse = await getIngredientList(recipe.recipe_id, search);
        if (json.success !== "1")
          throw new Error(json.message || "Failed to fetch ingredients");
        setAllIngredients(json.data.map(normaliseIngredient));
        setTotalIngredientCount(json.total_data);
      } catch (err: any) {
        setIngredientError(err.message ?? "Something went wrong");
      } finally {
        setLoadingIngredients(false);
      }
    },
    [recipe.recipe_id],
  );

  // Fetch on mount and whenever debounced search changes
  useEffect(() => {
    fetchIngredients(debouncedSearch);
  }, [fetchIngredients, debouncedSearch]);

  // ─── Stat items ───────────────────────────────────────────────────────────
  const statItems = [
    {
      label: "Cal",
      value: `${recipe.calories.toFixed(2)}kcal`,
      color: "#B44AE6",
    },
    { label: "Carb", value: `${recipe.carbs.toFixed(2)}g`, color: "#FFB532" },
    { label: "Fats", value: `${recipe.fat.toFixed(2)}g`, color: "#F25BEC" },
    { label: "A.Pro", value: `${aProt.toFixed(2)}g`, color: "#6BBDFA" },
    { label: "GL", value: `${gl.toFixed(2)}`, color: "#0DB5B5" },
    { label: "C:P:F", value: cpfDisplay, color: "#007AFF", bold: true },
  ];

  const tableColumns = [
    { label: "Name", cls: "w-[22%] pl-5 sm:pl-6" },
    { label: "Quantity", cls: "w-[10%]" },
    { label: "Unit", cls: "w-[12%]" },
    { label: "Cal", cls: "w-[10%]" },
    { label: "Carb", cls: "w-[9%]" },
    { label: "Fats", cls: "w-[9%]" },
    { label: "A.Prot", cls: "w-[9%]" },
    { label: "GL", cls: "w-[9%]" },
    // { label: "Oil", cls: "w-[10%] pr-5 sm:pr-6" },
  ];

  // ─── Base64 Decode (same as backend decodeText) ─────────────────────
function decodeBase64Text(encodedText: string): string {
  try {
    // atob converts base64 → binary string
    const binary = atob(encodedText);

    // convert binary → UTF-8 string
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch (e) {
    console.error("Base64 decode failed:", e);
    return encodedText; // fallback (show raw if error)
  }
}

  return (
    <>
      <style>{shimmerStyles}</style>

      <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
        {/* Page header */}
        <div className="flex-shrink-0">
          <PageHeader
            title="Log details"
            breadcrumb="Clients > Log details > Recipe"
            breadcrumbLinks={["/client", `/client/${userId}`, ""]}
          />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar-overlay scrollbar-visible px-6 space-y-4 pb-4">
          <div className="flex gap-4">
            {/* ── LEFT COLUMN ──────────────────────────────────────────────── */}
            <div className="w-[180px] bg-white rounded-[6px] border border-[#F3F4F6] overflow-hidden flex flex-col">
              <div className="h-[176px] w-full rounded-md overflow-hidden bg-[#F3F4F6]">
                {(() => {
                  const src = recipe.recipe_image
                    ? recipe.recipe_image.startsWith("http")
                      ? recipe.recipe_image
                      : `${BASE_URL}${recipe.recipe_image}`
                    : null;

                  return src ? (
                    <img
                      src={src}
                      alt={recipe.recipe_name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M8 16l2.5-3.5L13 15l2-2.5L18 16H8z"
                          fill="#9CA3AF"
                        />
                        <circle cx="9.5" cy="10.5" r="1.5" fill="#9CA3AF" />
                      </svg>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ── RIGHT COLUMN ─────────────────────────────────────────────── */}
            <div className="flex-1 flex flex-col gap-4">
              {/* SUMMARY CARD */}
              <div className="bg-white rounded-[6px] border border-[#F3F4F6] px-4 py-3">
                <div className="flex items-center justify-start gap-1.5 mb-3">
                  <span className="font-medium text-base 3xl:text-lg font-inter text-[#1F1F1F]">
                    {recipe.recipe_name}
                  </span>
                  <VegIcon isVeg={recipe.is_veg} />
                </div>
                <div className="border-t-[0.5px] border-[#F3F4F6] mb-3 -mx-4" />
                {/* 4-column layout */}
                <div className="grid grid-cols-4 gap-x-2">
                  <div>
                    <p className="text-[10px] 3xl:text-xs font-inter font-medium text-[#646464] uppercase tracking-wide mb-1.5">
                      Duration
                    </p>
                    <p className="text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F]">
                      {recipe.cooking_time || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] 3xl:text-xs font-inter font-medium text-[#646464] uppercase tracking-wide mb-1.5">
                      Per serving size
                    </p>
                    <p className="text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F]">
                      {recipe.serving_size || "-"}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] 3xl:text-xs font-inter font-medium text-[#646464] uppercase tracking-wide mb-1.5">
                      Meal time
                    </p>
                    <TruncatedList items={recipe.meal_times} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] 3xl:text-xs font-inter font-medium text-[#646464] uppercase tracking-wide mb-1.5">
                      Cooking type
                    </p>
                    <TruncatedList items={recipe.cooking_types} />
                  </div>
                </div>
              </div>

              {/* STATS CARD */}
              <div className="bg-white rounded-[6px] border border-[#F3F4F6] overflow-hidden flex items-center">
                {statItems.map(({ label, value, color }) => (
                  <div
                    key={label}
                    className="flex items-center gap-1.5 px-5 py-3.5 flex-1 justify-items-stretch"
                  >
                    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                      {label} -
                    </span>
                    <span
                      className="text-sm 3xl:text-base font-inter font-semibold"
                      style={{ color }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Method ─────────────────────────────────────────────────────── */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] p-6">
            <h3 className="text-base 3xl:text-lg font-semibold text-[#1F1F1F] font-inter mb-4">
              Method
            </h3>
            <div className="max-h-[160px] overflow-y-auto overflow-x-hidden custom-scrollbar-overlay scrollbar-visible space-y-3 pr-2 -mr-3">
              {method ? (
                method.split("\n\n").map((para, i) => (
                  <p
                    key={i}
                    className="text-sm 3xl:text-base text-[#646464] font-inter leading-relaxed break-words"
                  >
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-sm text-[#9CA3AF] font-inter italic">
                  No method provided.
                </p>
              )}
            </div>
          </div>

          {/* ── Ingredient list ─────────────────────────────────────────────── */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] overflow-hidden">
            {/* Header row */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4">
              <h3 className="text-lg font-semibold text-[#1F1F1F] font-inter">
                Total ingredient list ({totalIngredientCount})
              </h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-56 pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm font-inter focus:outline-none focus:ring-1 focus:ring-teal-400"
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px]">
                {/* Column headers — always visible */}
                <thead>
                  <tr className="border-t border-b border-[#111C2D1A]">
                    {tableColumns.map(({ label, cls }) => (
                      <th
                        key={label}
                        className={`py-3 text-left text-sm text-[#646464] font-inter font-normal ${cls}`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* ── Loading shimmer rows ── */}
                  {loadingIngredients && (
                    <tr>
                      <td colSpan={9} className="p-0">
                        <div className="shimmer h-[200px] w-full" />
                      </td>
                    </tr>
                  )}

                  {/* ── Error state ── */}
                  {!loadingIngredients && ingredientError && (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-12 text-center"
                      >
                        <p className="text-red-400 text-sm font-inter mb-2">
                          {ingredientError}
                        </p>
                        <button
                          onClick={() => fetchIngredients(debouncedSearch)}
                          className="text-sm text-[#0DB5B5] underline font-inter"
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  )}

                  {/* ── Empty state ── */}
                  {!loadingIngredients &&
                    !ingredientError &&
                    allIngredients.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="py-12 text-center text-sm text-gray-400 font-inter"
                        >
                          {debouncedSearch
                            ? "No ingredients match your search"
                            : "No ingredients found"}
                        </td>
                      </tr>
                    )}

                  {/* ── Data rows ── */}
                  {!loadingIngredients &&
                    !ingredientError &&
                    allIngredients.map((ing, index) => (
                      <tr
                        key={ing.id}
                        className={`hover:bg-[#F9FEFE] transition-colors ${
                          index !== allIngredients.length - 1
                            ? "border-b border-[#111C2D1A]"
                            : ""
                        }`}
                      >
                        <td className="py-3 pl-5 sm:pl-6 w-[22%]">
                          <div className="flex items-center gap-3">
                            <IngredientThumb
                              name={ing.name}
                              image={ing.image}
                            />
                            <span className="text-sm text-[#1F1F1F] font-inter font-medium">
                              {ing.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.quantity}
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.unit}
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.cal}
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.carb}
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.fats}
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.aProt}
                        </td>
                        <td className="py-3 text-sm text-[#1F1F1F] font-inter">
                          {ing.gl}
                        </td>
                        {/* <td className="py-3 pr-5 sm:pr-6 text-sm text-[#1F1F1F] font-inter">
                          {ing.oil}
                        </td> */}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecipeDetailPage;